import React, { useMemo, useState } from 'react';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { useAuth } from '@/contexts/AuthContext';
import { useOrdenesTrabajoContext } from '@/contexts/OrdenesTrabajoContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, MapPin, RefreshCw, Plus, Phone, Edit, ClipboardList, Home, LogOut, Share2, Copy, Check, Search, FileSpreadsheet, ArrowUpDown, Filter, Archive, ArchiveRestore, MoreVertical, Star, LayoutGrid, List, Shield } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ImportarClientesDialog } from '@/components/clientes/ImportarClientesDialog';
import { HoldedSyncAllButton } from '@/components/clientes/HoldedSyncControls';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useClientesFavoritos } from '@/hooks/useClientesFavoritos';
import { cn } from '@/lib/utils';
import { getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { getSystemTypeIcon } from '@/lib/systemTypeIcons';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const Clientes = () => {
  const navigate = useNavigate();
  const { signOut, perfil } = useAuth();
  const { 
    clientes, 
    sistemas,
    setClienteActual, 
    cargarClientes, 
    eliminarCliente,
    isLoading 
  } = useSecurityContext();
  
  // For technicians, get only clients with assigned work orders
  const { getClientesAsignados } = useOrdenesTrabajoContext();
  const clientesAsignados = perfil?.rol === 'tecnico' ? getClientesAsignados() : [];

  const [copiado, setCopiado] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('nombre-asc');
  const [filterPoblacion, setFilterPoblacion] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [soloFavoritos, setSoloFavoritos] = useState(false);
  const [tipoFilter, setTipoFilter] = useState<'cliente' | 'proveedor' | 'todos'>('cliente');
  const [sistemaFilter, setSistemaFilter] = useState<string>(() => {
    if (typeof window === 'undefined') return 'all';
    return localStorage.getItem('clientes-sistema-filter') || 'all';
  });

  const cambiarSistemaFilter = (valor: string) => {
    setSistemaFilter(valor);
    try { localStorage.setItem('clientes-sistema-filter', valor); } catch {}
  };

  // Mapa cliente_id -> tipos de sistema (de cualquier sede)
  const sistemasPorCliente = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    for (const s of sistemas as any[]) {
      if (!s?.cliente_id || !s?.tipo) continue;
      const tipos = map.get(s.cliente_id) ?? new Map<string, number>();
      tipos.set(s.tipo, (tipos.get(s.tipo) || 0) + 1);
      map.set(s.cliente_id, tipos);
    }
    return map;
  }, [sistemas]);

  const tiposSistemaDisponibles = useMemo(() => {
    const set = new Set<string>();
    for (const tipos of sistemasPorCliente.values()) {
      for (const t of tipos.keys()) set.add(t);
    }
    return [...set].sort((a, b) => getSystemTypeLabel(a).localeCompare(getSystemTypeLabel(b)));
  }, [sistemasPorCliente]);

  const renderSistemaBadges = (clienteId: string, max = 4) => {
    const tipos = sistemasPorCliente.get(clienteId);
    if (!tipos || tipos.size === 0) return null;
    const entries = [...tipos.entries()].sort((a, b) =>
      getSystemTypeLabel(a[0]).localeCompare(getSystemTypeLabel(b[0]))
    );
    const visibles = entries.slice(0, max);
    const restantes = entries.length - visibles.length;
    return (
      <div className="flex flex-wrap gap-1">
        {visibles.map(([tipo, count]) => {
          const Icon = getSystemTypeIcon(tipo);
          return (
            <Badge
              key={tipo}
              variant={sistemaFilter === tipo ? 'default' : 'secondary'}
              className="text-[10px] gap-1 font-normal"
              title={getSystemTypeLabel(tipo)}
            >
              <Icon className="h-3 w-3" />
              <span className="truncate max-w-[120px]">{getSystemTypeLabel(tipo)}</span>
              {count > 1 && <span className="opacity-70">×{count}</span>}
            </Badge>
          );
        })}
        {restantes > 0 && (
          <Badge variant="outline" className="text-[10px] font-normal">+{restantes}</Badge>
        )}
      </div>
    );
  };


  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    if (typeof window === 'undefined') return 'grid';
    return (localStorage.getItem('clientes-view-mode') as 'grid' | 'list') || 'grid';
  });
  const { esFavorito, toggle: toggleFavorito } = useClientesFavoritos();
  const { toast } = useToast();

  const handleClienteClick = (cliente: any) => {
    setClienteActual(cliente);
    navigate(`/cliente/${cliente.id}`);
  };

  const handleNuevoChecklist = (cliente: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setClienteActual(cliente);
    // Navegamos con un parámetro para indicar que queremos crear un checklist
    navigate('/checklist?action=create');
  };

  const handleEliminar = async (id: string) => {
    await eliminarCliente(id);
  };

  const handleVolverMenuPrincipal = () => {
    navigate('/');
  };

  const cambiarViewMode = (modo: 'grid' | 'list') => {
    setViewMode(modo);
    try { localStorage.setItem('clientes-view-mode', modo); } catch {}
  };

  const handleCerrarSesion = async () => {
    await signOut();
    navigate('/login');
  };

  const enlacePublico = `${window.location.origin}/alta-cliente-cra`;

  const copiarEnlace = async () => {
    try {
      await navigator.clipboard.writeText(enlacePublico);
      setCopiado(true);
      toast({
        title: "Enlace copiado",
        description: "El enlace se ha copiado al portapapeles correctamente.",
      });
      setTimeout(() => setCopiado(false), 2000);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo copiar el enlace al portapapeles.",
        variant: "destructive",
      });
    }
  };

  const compartirEnlace = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Alta de Cliente CRA',
          text: 'Completa el formulario de alta de cliente CRA',
          url: enlacePublico,
        });
      } catch (error) {
        console.log('Error al compartir:', error);
      }
    } else {
      // Si no hay API de compartir nativo, copiamos al portapapeles
      copiarEnlace();
    }
  };

  // Filter clients based on user role and active status
  const availableClientes = (perfil?.rol === 'tecnico' 
    ? clientes.filter(cliente => 
        clientesAsignados.some(asignado => asignado.id === cliente.id)
      )
    : clientes
  ).filter(cliente => showArchived ? (cliente as any).activo === false : (cliente as any).activo !== false);

  const handleRestaurarCliente = async (clienteId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { error } = await supabase.from('clientes').update({ activo: true }).eq('id', clienteId);
      if (error) throw error;
      await cargarClientes();
      toast({ title: "Cliente restaurado", description: "El cliente ha sido restaurado correctamente." });
    } catch {
      toast({ title: "Error", description: "No se pudo restaurar el cliente.", variant: "destructive" });
    }
  };

  // Unique populations for filter
  const poblaciones = [...new Set(
    availableClientes
      .map(c => (c as any).poblacion)
      .filter(Boolean)
  )].sort() as string[];

  const filteredClientes = availableClientes
    .filter(cliente => {
      if (soloFavoritos && !esFavorito(cliente.id)) return false;
      if (sistemaFilter !== 'all') {
        const tipos = sistemasPorCliente.get(cliente.id);
        if (sistemaFilter === 'con-sistemas') {
          if (!tipos || tipos.size === 0) return false;
        } else if (sistemaFilter === 'sin-sistemas') {
          if (tipos && tipos.size > 0) return false;
        } else if (!tipos?.has(sistemaFilter)) {
          return false;
        }
      }
      if (filterPoblacion && filterPoblacion !== 'all' && (cliente as any).poblacion !== filterPoblacion) return false;
      const tipo = ((cliente as any).tipo_contacto || 'cliente') as string;
      if (tipoFilter === 'cliente' && tipo === 'proveedor') return false;
      if (tipoFilter === 'proveedor' && tipo !== 'proveedor' && tipo !== 'ambos') return false;
      const searchLower = searchTerm.toLowerCase();
      return (
        cliente.nombre.toLowerCase().includes(searchLower) ||
        (cliente.direccion && cliente.direccion.toLowerCase().includes(searchLower)) ||
        (cliente.telefono && cliente.telefono.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      // Favoritos siempre fijados arriba
      const favA = esFavorito(a.id) ? 1 : 0;
      const favB = esFavorito(b.id) ? 1 : 0;
      if (favA !== favB) return favB - favA;
      switch (sortBy) {
        case 'nombre-asc':
          return a.nombre.localeCompare(b.nombre);
        case 'nombre-desc':
          return b.nombre.localeCompare(a.nombre);
        case 'poblacion-asc':
          return ((a as any).poblacion || '').localeCompare((b as any).poblacion || '');
        case 'creacion-desc':
          return new Date((b as any).created_at || 0).getTime() - new Date((a as any).created_at || 0).getTime();
        case 'creacion-asc':
          return new Date((a as any).created_at || 0).getTime() - new Date((b as any).created_at || 0).getTime();
        default:
          return 0;
      }
    });

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4 md:p-6">
      <div className="max-w-6xl mx-auto mobile-spacing">

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground mobile-text">
            Gestión de Instalaciones de Seguridad
          </h1>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {perfil?.rol === "admin" && (
              <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="mobile-button shrink-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-popover z-50">
                  <DropdownMenuItem onClick={() => setImportDialogOpen(true)}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Importar desde Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ImportarClientesDialog
                open={importDialogOpen}
                onOpenChange={setImportDialogOpen}
                onSuccess={() => cargarClientes()}
              />
              </>
            )}
            <HoldedSyncAllButton />

            <Button 
              variant="default"
              onClick={() => navigate('/alta-cliente-cra')}
              className="flex items-center justify-center gap-2 mobile-button"
            >
              <Plus className="h-4 w-4" />
              Nuevo Cliente
            </Button>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex gap-1 mb-3 rounded-md border p-1 bg-muted/30 w-fit">
            {(['cliente','proveedor','todos'] as const).map(t => (
              <Button
                key={t}
                type="button"
                variant={tipoFilter === t ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setTipoFilter(t)}
                className="capitalize"
              >
                {t === 'cliente' ? 'Clientes' : t === 'proveedor' ? 'Proveedores' : 'Todos'}
              </Button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">

              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar clientes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <ArrowUpDown className="h-4 w-4 mr-2 shrink-0" />
                <SelectValue placeholder="Ordenar" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-popover">
                <SelectItem value="nombre-asc">Nombre A-Z</SelectItem>
                <SelectItem value="nombre-desc">Nombre Z-A</SelectItem>
                <SelectItem value="poblacion-asc">Población</SelectItem>
                <SelectItem value="creacion-desc">Más recientes</SelectItem>
                <SelectItem value="creacion-asc">Más antiguos</SelectItem>
              </SelectContent>
            </Select>
            {poblaciones.length > 0 && (
              <Select value={filterPoblacion} onValueChange={setFilterPoblacion}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <Filter className="h-4 w-4 mr-2 shrink-0" />
                  <SelectValue placeholder="Población" />
                </SelectTrigger>
                <SelectContent className="z-50 bg-popover">
                  <SelectItem value="all">Todas</SelectItem>
                  {poblaciones.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={sistemaFilter} onValueChange={cambiarSistemaFilter}>
              <SelectTrigger className="w-full sm:w-[220px]">
                <Shield className="h-4 w-4 mr-2 shrink-0" />
                <SelectValue placeholder="Sistema" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-popover">
                <SelectItem value="all">Todos los clientes</SelectItem>
                <SelectItem value="con-sistemas">Con algún sistema</SelectItem>
                <SelectItem value="sin-sistemas">Sin sistemas</SelectItem>
                {tiposSistemaDisponibles.map(t => (
                  <SelectItem key={t} value={t}>{getSystemTypeLabel(t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={soloFavoritos ? "default" : "outline"}
              size="sm"
              onClick={() => setSoloFavoritos(!soloFavoritos)}
              className="gap-2"
              title="Mostrar solo favoritos"
            >
              <Star className={cn("h-4 w-4", soloFavoritos && "fill-current")} />
              Favoritos
            </Button>
            <div className="flex rounded-md border overflow-hidden">
              <Button
                type="button"
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => cambiarViewMode('grid')}
                className="rounded-none"
                title="Vista en cuadrícula"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => cambiarViewMode('list')}
                className="rounded-none"
                title="Vista en lista"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            {(perfil?.rol === 'admin' || perfil?.rol === 'gestor') && (
              <Button
                variant={showArchived ? "default" : "outline"}
                size="sm"
                onClick={() => setShowArchived(!showArchived)}
                className="gap-2"
              >
                <Archive className="h-4 w-4" />
                {showArchived ? 'Ver activos' : 'Ver archivados'}
              </Button>
            )}
          </div>
          {(searchTerm || filterPoblacion || showArchived || sistemaFilter !== 'all') && (
            <p className="text-sm text-muted-foreground mt-2">
              {showArchived ? '📦 Mostrando clientes archivados — ' : ''}
              {filteredClientes.length} cliente{filteredClientes.length !== 1 ? 's' : ''}
              {sistemaFilter === 'con-sistemas'
                ? ' con algún sistema'
                : sistemaFilter === 'sin-sistemas'
                  ? ' sin sistemas'
                  : sistemaFilter !== 'all'
                    ? ` con sistema de ${getSystemTypeLabel(sistemaFilter)}`
                    : ` encontrado${filteredClientes.length !== 1 ? 's' : ''}`}
            </p>
          )}
        </div>

        {/* Lista de Clientes */}
        <div className={viewMode === 'grid' ? "grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "flex flex-col gap-2"}>
          {availableClientes.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Users className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No hay clientes disponibles</h3>
                <p className="text-muted-foreground text-center mb-4">
                  {perfil?.rol === 'tecnico' 
                    ? 'No tienes clientes asignados a través de órdenes de trabajo'
                    : 'Comienza agregando tu primer cliente con sistemas de seguridad'
                  }
                </p>
                <Button 
                  variant="default"
                  onClick={() => navigate('/alta-cliente-cra')}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Agregar Primer Cliente
                </Button>
              </CardContent>
            </Card>
          ) : filteredClientes.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Search className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No se encontraron clientes</h3>
                <p className="text-muted-foreground text-center mb-4">
                  No hay clientes que coincidan con "{searchTerm}"
                </p>
                <Button 
                  variant="outline"
                  onClick={() => setSearchTerm('')}
                  className="flex items-center gap-2"
                >
                  Limpiar búsqueda
                </Button>
              </CardContent>
            </Card>
          ) : viewMode === 'list' ? (
            filteredClientes.map((cliente) => (
              <div
                key={cliente.id}
                onClick={() => handleClienteClick(cliente)}
                className="cursor-pointer border rounded-lg bg-card hover:bg-accent/40 hover:border-primary/50 transition-colors px-3 py-2 flex items-center gap-3"
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={(e) => { e.stopPropagation(); toggleFavorito(cliente.id); }}
                  title={esFavorito(cliente.id) ? "Quitar de favoritos" : "Añadir a favoritos"}
                >
                  <Star className={cn("h-4 w-4", esFavorito(cliente.id) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground")} />
                </Button>
                <Users className="h-4 w-4 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    <span className="truncate">{cliente.nombre}</span>
                    {(() => {
                      const t = ((cliente as any).tipo_contacto || 'cliente') as string;
                      if (t === 'proveedor') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Proveedor</span>;
                      if (t === 'ambos') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">Cliente + Proveedor</span>;
                      return null;
                    })()}
                  </div>
                  <div className="text-xs text-muted-foreground truncate flex items-center gap-3">
                    {cliente.direccion && (
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{cliente.direccion}</span>
                    )}
                    {cliente.telefono && (
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{cliente.telefono}</span>
                    )}
                  </div>
                  <div className="mt-1">{renderSistemaBadges(cliente.id, 3)}</div>
                </div>
                {perfil?.rol === "admin" && (
                  <div className="flex gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => { e.stopPropagation(); navigate(`/cliente/${cliente.id}`); }}
                      title="Editar cliente"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {showArchived ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => handleRestaurarCliente(cliente.id, e)}
                        title="Restaurar cliente"
                      >
                        <ArchiveRestore className="h-4 w-4 text-green-500" />
                      </Button>
                    ) : (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => e.stopPropagation()}
                            title="Archivar cliente"
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Archivar cliente?</AlertDialogTitle>
                            <AlertDialogDescription>
                              El cliente "{cliente.nombre}" será archivado y dejará de aparecer en la lista activa. Sus datos, obras, sistemas y documentos se conservarán intactos. Podrás restaurarlo en cualquier momento.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleEliminar(cliente.id)}>
                              Archivar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            filteredClientes.map((cliente) => (
              <Card 
                key={cliente.id} 
                className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-primary/50 group"
                onClick={() => handleClienteClick(cliente)}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 flex-wrap">
                      <Users className="h-5 w-5 text-primary" />
                      <span>{cliente.nombre}</span>
                      {(() => {
                        const t = ((cliente as any).tipo_contacto || 'cliente') as string;
                        if (t === 'proveedor') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 font-normal">Proveedor</span>;
                        if (t === 'ambos') return <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 font-normal">Cliente + Proveedor</span>;
                        return null;
                      })()}
                    </CardTitle>
                    <div className="flex gap-1 items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorito(cliente.id);
                        }}
                        title={esFavorito(cliente.id) ? "Quitar de favoritos" : "Añadir a favoritos"}
                      >
                        <Star
                          className={cn(
                            "h-4 w-4",
                            esFavorito(cliente.id)
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-muted-foreground"
                          )}
                        />
                      </Button>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {perfil?.rol === "admin" && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/cliente/${cliente.id}`);
                            }}
                            title="Editar cliente"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {showArchived ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleRestaurarCliente(cliente.id, e)}
                              title="Restaurar cliente"
                            >
                              <ArchiveRestore className="h-4 w-4 text-green-500" />
                            </Button>
                          ) : (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => e.stopPropagation()}
                                  title="Archivar cliente"
                                >
                                  <Archive className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>¿Archivar cliente?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    El cliente "{cliente.nombre}" será archivado y dejará de aparecer en la lista activa. Sus datos, obras, sistemas y documentos se conservarán intactos. Podrás restaurarlo en cualquier momento.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction 
                                    onClick={() => handleEliminar(cliente.id)}
                                  >
                                    Archivar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </>
                      )}
                      </div>
                    </div>
                  </div>
                  <CardDescription className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {cliente.direccion || 'Sin dirección'}
                  </CardDescription>
                  {cliente.telefono && (
                    <CardDescription className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {cliente.telefono}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-2">
                  {renderSistemaBadges(cliente.id)}
                  <p className="text-sm text-muted-foreground">
                    Clic para ver detalles del cliente →
                  </p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Clientes;